import React, { useState } from 'react';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import { apiClient } from 'services/api';
import { useForm, Controller } from 'react-hook-form';
import CoverLayout  from 'layouts/auth/components/CoverLayout';
import logoEma from 'assets/images/Portal-Cliente-Images/Logo-ema.png';
import SoftInputBase from 'components/SoftInputBase';
import { useAuth } from 'layouts/auth/AuthContext'; // Importa el hook useAuth del contexto de autenticación
import { API_BACK } from 'config';

// Motivos con los que el backend (routes/sso.py) devuelve al login si falla el ingreso con Microsoft
const SSO_ERRORES = {
  sin_alta: 'Tu cuenta de Microsoft no tiene usuario en el Portal Clientes. Pedí el alta a un administrador.',
  no_configurado: 'El ingreso con Microsoft no está disponible en este momento.',
  fallo: 'No se pudo iniciar sesión con Microsoft. Intentá de nuevo.',
};

const leerErrorSso = () => {
  const motivo = new URLSearchParams(window.location.search).get('sso_error');
  return motivo ? SSO_ERRORES[motivo] || SSO_ERRORES.fallo : '';
};

// Logo de Microsoft (cuatro cuadrados) para el botón de ingreso con cuenta de la empresa
const LogoMicrosoft = () => (
  <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true" style={{ marginRight: 8 }}>
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const SignIn = () => {
  const { handleSubmit, control, formState: { errors } } = useForm();
  const [loginError, setLoginError] = useState(leerErrorSso);
  const { login } = useAuth(); // Obtén la función login del contexto de autenticación

  const onSubmit = async (data) => {
    if (!data.userName || !data.password) {
      setLoginError('Por favor, complete todos los campos.');
      return;
    }

    const formData = new FormData();
    formData.append('data', JSON.stringify(data));

    try {
      const response = await apiClient.post('/api/login', formData);
      if (response.status === 200) {
        const userData = response.data; // Supongamos que response.data tiene la estructura { usuarios: [...] }
        //console.log("Datos completos:", userData.data, userData.data[0]);

         // Suponiendo que la API d evuelve los datos del usuario en response.data
        login(userData.data[0]);  // Llama a la función de login del contexto con los datos del usuario
        document.location = '/home'; // Redirige al usuario después del inicio de sesión
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          setLoginError('Nombre de usuario o contraseña incorrectos.');
        } else if (error.response.status === 500) {
          setLoginError('Error del servidor. Por favor, intente de nuevo más tarde.');
        } else {
          setLoginError('Error desconocido. Por favor, intente de nuevo.');
        }
      } else {
        setLoginError('Error de red. Por favor, verifique su conexión.');
      }
    }
  };

  return (
    <CoverLayout
      title={"Bienvenido a Portal Clientes"}
      image={logoEma}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <SoftBox mb={1.5}>
          <SoftTypography component="label" variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
            Nombre de Usuario
          </SoftTypography>
          <Controller
            name="userName"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <SoftInputBase
                type="text"
                placeholder="Usuario"
                field={field}
              />
            )}
          />
          {errors.userName && <SoftTypography variant="caption" color="error">Campo requerido</SoftTypography>}
        </SoftBox>

        <SoftBox mb={1.5}>
          <SoftTypography component="label" variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
            Contraseña
          </SoftTypography>
          <Controller
            name="password"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <SoftInputBase
                type="password"
                placeholder="Contraseña"
                field={field}
              />
            )}
          />
          {errors.password && <SoftTypography variant="caption" color="error">Campo requerido</SoftTypography>}
        </SoftBox>

        {loginError && (
          <SoftBox mb={1.5}>
            <SoftTypography variant="caption" color="error">
              {loginError}
            </SoftTypography>
          </SoftBox>
        )}

        <SoftBox mt={3}>
          <SoftButton variant="gradient" color="info" type="submit" fullWidth>
            Iniciar Sesión
          </SoftButton>
        </SoftBox>
      </form>

      {/* Solo para empleados con usuario en el portal: navegación completa (no axios)
          porque el backend redirige a la pantalla de login de Microsoft */}
      <SoftBox display="flex" alignItems="center" my={2}>
        <SoftBox flex={1} sx={{ borderTop: '1px solid #d2d6da' }} />
        <SoftTypography variant="caption" color="text" px={1.5}>o</SoftTypography>
        <SoftBox flex={1} sx={{ borderTop: '1px solid #d2d6da' }} />
      </SoftBox>
      <SoftButton
        variant="outlined"
        color="dark"
        fullWidth
        onClick={() => { window.location.href = `${API_BACK}/api/auth/sso/login`; }}
      >
        <LogoMicrosoft />
        Ingresar con Microsoft
      </SoftButton>
    </CoverLayout>
  );
};

export default SignIn;
